import { http, HttpResponse } from 'msw';
import { mockFarmingRecords, mockPesticides, mockVideoMonitors, mockHarvestRecords } from '../fixtures/farming';
import type { FarmingRecord, PesticideDetail, HarvestRecord, HarvestStatsData } from '@trace/shared/types';

let farmingStore = [...mockFarmingRecords];
let pesticidesStore = [...mockPesticides];
let harvestsStore = [...mockHarvestRecords];

export const farmingHandlers = [
  // List Farming Records
  http.get('/api/farming/records', ({ request }) => {
    const url = new URL(request.url);
    const stage = url.searchParams.get('stage');
    const landId = url.searchParams.get('landId');

    let filtered = [...farmingStore];
    if (stage && stage !== 'all') {
      filtered = filtered.filter((r) => r.stage === stage);
    }
    if (landId && landId !== 'all') {
      filtered = filtered.filter((r) => r.landId === landId);
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list: filtered,
        total: filtered.length,
        page: 1,
        pageSize: 50,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Create Farming Record
  http.post('/api/farming/records', async ({ request }) => {
    const body = (await request.json()) as Partial<FarmingRecord>;
    const newRecord: FarmingRecord = {
      id: `farm_${Date.now()}`,
      landId: body.landId || 'land_01',
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      stage: body.stage || 'growing',
      stageName: body.stageName || '田间生长',
      operationDate: body.operationDate || new Date().toISOString(),
      operator: body.operator || '李春山',
      temperature: body.temperature || 25,
      humidity: body.humidity || 70,
      weather: body.weather || '晴天',
      workDescription: body.workDescription || '',
      pesticides: body.pesticides || [],
      fertilizers: body.fertilizers || [],
      photos: body.photos || [],
      harvestRecord: body.harvestRecord,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    farmingStore = [newRecord, ...farmingStore];

    return HttpResponse.json({
      code: 200,
      message: '农事台账录入成功',
      data: newRecord,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // List Pesticides with Land filter
  http.get('/api/farming/pesticides', ({ request }) => {
    const url = new URL(request.url);
    const landId = url.searchParams.get('landId');
    const keyword = url.searchParams.get('keyword')?.toLowerCase();

    let list = [...pesticidesStore];
    if (landId && landId !== 'all') {
      list = list.filter((p) => p.landId === landId);
    }
    if (keyword) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(keyword) ||
          (p.purpose && p.purpose.toLowerCase().includes(keyword)) ||
          p.operator.toLowerCase().includes(keyword) ||
          (p.landName && p.landName.toLowerCase().includes(keyword)),
      );
    }

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Add/Update Pesticide Detail
  http.post('/api/farming/pesticides', async ({ request }) => {
    const body = (await request.json()) as Partial<PesticideDetail>;
    const newPest: PesticideDetail = {
      id: `pest_${Date.now()}`,
      landId: body.landId || 'land_01',
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      landCode: body.landCode || 'LAND-2026-A01',
      name: body.name || '0.5% 苦参碱水剂',
      inputType: body.inputType || '生物植物源农药',
      standardCode: body.standardCode || 'GB/T 19630',
      standard: body.standard || 'GB/T 19630 有机标准允许使用',
      certNumber: body.certNumber || body.organicCertNo || 'OFDC-BIO-2026-088',
      organicCertNo: body.organicCertNo || body.certNumber || 'OFDC-BIO-2026-088',
      dosagePerMu: body.dosagePerMu || 100,
      unit: body.unit || 'ml/亩',
      safetyInterval: body.safetyInterval ?? body.safetyIntervalDays ?? 7,
      safetyIntervalDays: body.safetyIntervalDays ?? body.safetyInterval ?? 7,
      usageDate: body.usageDate || (body.usageTime ? body.usageTime.slice(0, 10) : new Date().toISOString().slice(0, 10)),
      usageTime: body.usageTime || body.usageDate || new Date().toISOString().slice(0, 10),
      operator: body.operator || '李春山',
      purpose: body.purpose || '水稻绿色生物防控',
      remarks: body.remarks || '',
    };

    pesticidesStore = [newPest, ...pesticidesStore];

    return HttpResponse.json({
      code: 200,
      message: '用药记录保存成功',
      data: newPest,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // List Harvest Records with stats
  http.get('/api/farming/harvests', ({ request }) => {
    const url = new URL(request.url);
    const landId = url.searchParams.get('landId');
    const qualityGrade = url.searchParams.get('qualityGrade');
    const keyword = url.searchParams.get('keyword')?.toLowerCase();

    let list = [...harvestsStore];
    if (landId && landId !== 'all') {
      list = list.filter((h) => String(h.landId) === String(landId));
    }
    if (qualityGrade && qualityGrade !== 'all') {
      list = list.filter((h) => (h.qualityGrade || 'grade_1') === qualityGrade);
    }
    if (keyword) {
      list = list.filter(
        (h) =>
          h.landName.toLowerCase().includes(keyword) ||
          (h.storageBatchNo && h.storageBatchNo.toLowerCase().includes(keyword)) ||
          (h.storageBatchNumber && h.storageBatchNumber.toLowerCase().includes(keyword)) ||
          h.operator.toLowerCase().includes(keyword),
      );
    }

    // Compute stats
    const totalYieldKg = list.reduce((acc, h) => acc + (Number(h.totalYield) || 0), 0);
    const totalArea = list.reduce((acc, h) => acc + (Number(h.area) || 0), 0);
    const avgYieldPerMu = totalArea > 0 ? Number((totalYieldKg / totalArea).toFixed(1)) : 0;
    const processedWeightKg = list.reduce((acc, h) => acc + (Number(h.processedWeight) || 0), 0);
    const inStockPaddyKg = totalYieldKg - processedWeightKg;
    const producedRiceKg = Math.round(processedWeightKg * 0.66); // 66% yield applied to processed paddy, not stock

    const stats: HarvestStatsData = {
      avgYieldPerMu,
      totalYieldKg,
      totalYieldTons: Number((totalYieldKg / 1000).toFixed(1)),
      processedWeightKg,
      producedRiceKg,
      inStockPaddyKg,
    };

    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: {
        list,
        total: list.length,
        stats,
      },
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // Create Harvest Record
  http.post('/api/farming/harvests', async ({ request }) => {
    const body = (await request.json()) as Partial<HarvestRecord>;
    const totalYield = body.totalYield || ((Number(body.yieldPerMu) || 480) * (Number(body.area) || 120));
    const batchCode = body.storageBatchNo || body.storageBatchNumber || `PADDY-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-00${harvestsStore.length + 1}`;
    const newHarvest: HarvestRecord = {
      id: `hr_${Date.now()}`,
      landId: body.landId || 'land_01',
      landName: body.landName || '五常核心有机基地 · 1号稻田A区',
      landCode: body.landCode || 'LAND-2026-A01',
      harvestDate: body.harvestDate ? body.harvestDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
      yieldPerMu: body.yieldPerMu || 480,
      area: body.area || 120,
      totalYield,
      processedWeight: 0,
      moistureContent: body.moistureContent || 14.2,
      impurityRate: body.impurityRate || 0.8,
      pureGrainRate: 98.5,
      storageBatchNo: batchCode,
      storageBatchNumber: batchCode,
      warehouseId: body.warehouseId || body.warehouse || '五常1号恒温智能原粮仓',
      warehouse: body.warehouse || body.warehouseId || '五常1号恒温智能原粮仓',
      operator: body.operator || '李春山',
      remarks: body.remarks || '',
      photos: body.photos || [],
    };

    harvestsStore = [newHarvest, ...harvestsStore];

    return HttpResponse.json({
      code: 200,
      message: '秋季收割测产登记成功',
      data: newHarvest,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),

  // List Video Monitors
  http.get('/api/farming/videos', () => {
    return HttpResponse.json({
      code: 200,
      message: 'success',
      data: mockVideoMonitors,
      success: true,
      timestamp: new Date().toISOString(),
    });
  }),
];
